import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { notifyMessage } from '../../../shared/constants/notify-message';
import { Flexible } from '../../../shared/interfaces/create-surveys.interface';
import { SelectedQuota } from '../../../shared/interfaces/quota.interface';
import { QuotaModalService } from '../quota-modal.service';
import { Constants } from '../../../shared/constants/create-survey.constants';
import { ToasterService } from '@purespectrum1/ui/toaster-service';

@Component({
  selector: 'ps-ui-child-age-gender-row',
  templateUrl: './child-age-gender-row.component.html',
  styleUrls: ['./child-age-gender-row.component.css'],
})
export class ChildAgeGenderRowComponent implements OnInit, OnChanges {
  @Input() data!: any;
  @Input() flexible!: Flexible;
  @Output() readonly quotasChanged = new EventEmitter();
  quotas = this._fb.array([]);

  public hasNoChildrenCtr: boolean = true;
  public hasHaveChildrenCtr: boolean = true;

  form = this._fb.group({
    quotas: this.quotas,
    ageUnit: new FormControl(311, Validators.required),
  });

  constructor(
    private _fb: FormBuilder,
    private _toastrService: ToasterService,
    private _quotaModalService: QuotaModalService
  ) {}

  ngOnInit() {
    if (this.data?.quotas?.length) {
      this._createQuotaRow(this.data.quotas);
    } else {
      this._addQuotaDefault();
    }
    this.hasNoChildrenCtr = !!this.data.qualification.data.some(
      (q: ChildQualificationItem) =>
        q.id === Constants.CHILDREN.NO_CHILDREN && q.selected
    );
    this.hasHaveChildrenCtr = !!this.data.qualification.data.some(
      (q: ChildQualificationItem) =>
        q.id === Constants.CHILDREN.HAVE_CHILDREN && q.selected
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data?.currentValue?.quotas?.length) {
      this._updateQuotaRow(changes.data.currentValue.quotas);
    }

    if (changes.flexible?.currentValue) {
      this.flexible = changes.flexible.currentValue;
    }
  }

  private _updateQuotaRow(updateQuotas: SelectedQuota[]) {
    this.data.quotas = updateQuotas;
    const control: FormArray = <FormArray>this.form.get('quotas');
    if (control.controls.length) {
      updateQuotas.forEach((_quota: SelectedQuota, index: number) => {
        if (control.controls[index]) {
          control.controls[index].patchValue(_quota);
        }
      });
    }
  }

  private _addQuotaDefault() {
    const control: FormArray = <FormArray>this.form.get('quotas');
    control.push(this.newFormGroupWithChildren());
    control.push(this.newFormGroupNoChildren());
  }

  private _createQuotaRow(quotas: SelectedQuota[]) {
    const control: FormArray = <FormArray>this.form.get('quotas');
    const formControlAgeUnit: FormControl = <FormControl>(
      this.form.get('ageUnit')
    );
    const haveChildrenQuota = quotas.find((quota) => quota.ageunit);
    if (haveChildrenQuota) {
      formControlAgeUnit.patchValue(Number(haveChildrenQuota.ageunit));
    }

    this._addDefaultRowIfNotExist(quotas, control);

    let noChildrenExist = false;
    let noChildrenQuota = {} as SelectedQuota;
    quotas.forEach((quota: SelectedQuota, index: number) => {
      if (quota.text === 'No Children') {
        noChildrenExist = true;
        noChildrenQuota = quota;
      } else {
        const defaultNoneValue = index ? 0 : null;
        let rowFormQuota = new FormGroup({
          buyer_quota_id: new FormControl(quota.buyer_quota_id || ''),
          min: new FormControl(
            Number(quota.min) || defaultNoneValue,
            Validators.required
          ),
          max: new FormControl(
            Number(quota.max) || defaultNoneValue,
            Validators.required
          ),
          number: new FormControl(
            Number(quota.number) || defaultNoneValue,
            Validators.required
          ),
          percentage: new FormControl(
            Number(quota.percentage) || defaultNoneValue,
            Validators.required
          ),
          minimum: new FormControl(
            Number(quota.minimum) || defaultNoneValue,
            Validators.required
          ),
          maximum: new FormControl(
            Number(quota.maximum) || defaultNoneValue,
            Validators.required
          ),
          gender: new FormControl(quota.gender || null, Validators.required),
          text: new FormControl('Have Children', Validators.required),
          ageunit: <FormControl>this.form.get('ageUnit'),
          suppliers: new FormControl(quota.suppliers || []),
        });
        control.push(rowFormQuota);
      }
    });

    if (noChildrenExist) {
      this._addNoChildRow(noChildrenQuota, control);
    } else {
      control.push(this.newFormGroupNoChildren());
    }
    this.quotasChanged.emit(this._filterEmptyQuotas(this.form.value.quotas));
  }

  private _addDefaultRowIfNotExist(
    quotas: SelectedQuota[],
    control: FormArray
  ) {
    const first = quotas.find((quota) => quota.text !== 'No Children');

    if (!!first && !!first.min && !!first.max && !!first.percentage) {
      control.push(this.newFormGroupWithChildren());
    }
  }

  _addNoChildRow(quota: SelectedQuota, control: FormArray) {
    let rowForm = new FormGroup({
      buyer_quota_id: new FormControl(quota.buyer_quota_id || ''),
      min: new FormControl(null),
      max: new FormControl(null),
      number: new FormControl(
        Number(quota.number) || null,
        Validators.required
      ),
      percentage: new FormControl(
        Number(quota.percentage) || null,
        Validators.required
      ),
      minimum: new FormControl(
        Number(quota.minimum) || null,
        Validators.required
      ),
      maximum: new FormControl(
        Number(quota.maximum) || null,
        Validators.required
      ),
      text: new FormControl('No Children', Validators.required),
      gender: new FormControl(null),
    });
    control.push(rowForm);
  }

  newFormGroupNoChildren() {
    return new FormGroup({
      min: new FormControl(null),
      max: new FormControl(null),
      gender: new FormControl(null),
      percentage: new FormControl(null, Validators.required),
      number: new FormControl(null, Validators.required),
      minimum: new FormControl(null, Validators.required),
      maximum: new FormControl(null, Validators.required),
      text: new FormControl('No Children', Validators.required),
    });
  }

  newFormGroupWithChildren() {
    return new FormGroup({
      min: new FormControl(null, Validators.required),
      max: new FormControl(null, Validators.required),
      number: new FormControl(null, Validators.required),
      gender: new FormControl(null, Validators.required),
      percentage: new FormControl(null, Validators.required),
      minimum: new FormControl(null, Validators.required),
      maximum: new FormControl(null, Validators.required),
      text: new FormControl('Have Children', Validators.required),
      ageunit: <FormControl>this.form.get('ageUnit'),
    });
  }

  addQuotaRow() {
    const control: FormArray = <FormArray>this.form.get('quotas');
    if (!control.controls[0].value.gender) {
      this._toastrService.error(
        notifyMessage.errorMessage.QUOTA_MODAL.MISSING_GENDER
      );
      return;
    }
    if (
      control.controls[0] &&
      // min accept zero
      (!Number.isInteger(control.controls[0].value.min) ||
        !control.controls[0].value.max ||
        !control.controls[0].value.number ||
        !control.controls[0].value.percentage)
    ) {
      this._toastrService.error(
        notifyMessage.errorMessage.QUOTA_MODAL.EMPTY_RANGE_ROW
      );
      return;
    }

    const rowForm = this.newFormGroupWithChildren();
    if (
      this._quotaModalService.isValidAllocation(
        this.data.complete,
        control,
        0,
        false
      ) &&
      this._quotaModalService.validateMinMaxAge(control, 0)
    ) {
      control.insert(0, rowForm);
      this.quotasChanged.emit(this._filterEmptyQuotas(this.form.value.quotas));
    }
  }

  deleteQuotaRow(index: number) {
    const control: FormArray = <FormArray>this.form.get('quotas');
    control.removeAt(index);
    this.quotasChanged.emit(this._filterEmptyQuotas(this.form.value.quotas));
  }

  numberChange(event: Event, index: number) {
    let value = Number((<HTMLInputElement>event.target).value);
    const control: FormArray = <FormArray>this.form.get('quotas');

    if (
      index !== control.controls.length - 1 ||
      this._quotaModalService.isValidAllocation(
        this.data.complete,
        control,
        index,
        false
      )
    ) {
      let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
        value,
        this.flexible
      );
      control.controls[index].patchValue({
        percentage: Math.round((value * 100) / Number(this.data.complete)),
        minimum: minimum,
        maximum: maximum,
      });
    }
    this.quotasChanged.emit(this._filterEmptyQuotas(this.form.value.quotas));
  }

  percentageChange(event: Event, index: number) {
    let value = Number((<HTMLInputElement>event.target).value);
    const control: FormArray = <FormArray>this.form.get('quotas');

    if (
      index !== control.controls.length - 1 ||
      this._quotaModalService.isValidAllocation(
        this.data.complete,
        control,
        index,
        false
      )
    ) {
      const number = this._quotaModalService.getNumber(
        this.data.complete,
        this.form.value.quotas,
        value,
        index
      );
      let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
        number,
        this.flexible
      );
      control.controls[index].patchValue({
        number: number,
        minimum: minimum,
        maximum: maximum,
      });
    }

    this.quotasChanged.emit(this._filterEmptyQuotas(this.form.value.quotas));
  }

  selectAgeUnit() {
    const control: FormArray = <FormArray>this.form.get('quotas');
    if (control.controls.length > 2) {
      if (confirm('Modifying the Unit Name will delete the quotas')) {
        while (control.length !== 0) {
          control.removeAt(0);
        }
        control.push(this.newFormGroupNoChildren());

        const rowForm = this.newFormGroupWithChildren();

        control.insert(0, rowForm);
      } else {
        const formControlAgeUnit: FormControl = <FormControl>(
          this.form.get('ageUnit')
        );
        const valueToSet = formControlAgeUnit.value === 311 ? 312 : 311;
        formControlAgeUnit.patchValue(valueToSet);
      }
    }
  }

  private _filterEmptyQuotas(quotas: any) {
    return quotas.map((quota: any, index: number) => {
      if (index) {
        if (quota.ageunit) {
          quota.ageunit = Number(quota.ageunit);
        }
        quota.percentage = Number(quota.percentage);
        quota.number = Number(quota.number);
        quota.maximum = Number(quota.maximum);
        quota.minimum = Number(quota.minimum);
      }
      return quota;
    });
  }
}

interface ChildQualificationItem {
  id: number;
  text: string;
  sub_text: string;
  selected: boolean;
}
